<script setup>
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue"
import { computed, ref, onMounted } from "vue"
import { usePage } from "@inertiajs/vue3"

const props = defineProps({
    wallet: {
        type: Object,
        required: true
    },
    settings: {
        type: Object,
        default: () => ({})
    }
})

const page = usePage()
const currencySymbol = computed(() => page.props.currencySymbol || '$')
const isAnimated = ref(false)

onMounted(() => {
    setTimeout(() => {
        isAnimated.value = true
    }, 100)
})

const formatCurrency = (value) => {
    if (value === null || value === undefined) return `${currencySymbol.value}0.00`
    const num = parseFloat(value) || 0
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num)
}

const formatCompact = (value) => {
    if (value === null || value === undefined) return '0'
    const num = parseFloat(value) || 0
    if (num >= 1000000) {
        return (num / 1000000).toFixed(2) + 'M'
    } else if (num >= 1000) {
        return (num / 1000).toFixed(2) + 'K'
    }
    return num.toFixed(2)
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const getDaysRemaining = (unlockDate) => {
    if (!unlockDate) return 0
    const now = new Date()
    const unlock = new Date(unlockDate)
    const diffTime = unlock - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays > 0 ? diffDays : 0
}

const getHoursRemaining = (unlockDate) => {
    if (!unlockDate) return 0
    const now = new Date()
    const unlock = new Date(unlockDate)
    const diffTime = unlock - now
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60))
    return diffHours > 0 ? diffHours : 0
}

const getUnlockPercentage = (unlockDate, lockDays) => {
    if (!unlockDate || !lockDays) return 100
    const now = new Date()
    const unlock = new Date(unlockDate)
    if (Number.isNaN(unlock.getTime())) return 0
    const daysRemaining = getDaysRemaining(unlockDate)
    const percentage = ((lockDays - daysRemaining) / lockDays) * 100
    return Math.min(Math.max(percentage, 0), 100)
}

const availableTokens = computed(() => {
    // DS-Credits are stored in `credit_balance` — use that for available DS-Credits.
    return parseFloat(props.wallet.credit_balance || 0)
})

const lockedTokens = computed(() => {
    return parseFloat(props.wallet.locked_tokens || 0)
})

const totalTokens = computed(() => {
    return parseFloat(props.wallet.credit_balance || props.wallet.token_balance || 0)
})

const hasLockedTokens = computed(() => {
    return lockedTokens.value > 0 && props.wallet.tokens_unlock_at
})

const hasActiveLock = computed(() => {
    if (typeof props.wallet.has_active_lock === 'boolean') {
        return props.wallet.has_active_lock
    }
    return lockedTokens.value > 0 && !!props.wallet.tokens_unlock_at
})

const lockProgressPercentage = computed(() => {
    const serverProgress = parseFloat(props.wallet.lock_progress_percentage)
    if (!Number.isNaN(serverProgress)) {
        return Math.min(Math.max(serverProgress, 0), 100)
    }
    return getUnlockPercentage(props.wallet.tokens_unlock_at, parseFloat(props.wallet.lock_days || 0))
})

const availablePercentage = computed(() => {
    if (totalTokens.value === 0) return 100
    return (availableTokens.value / totalTokens.value) * 100
})

const totalEarnings = computed(() => {
    return parseFloat(props.wallet.total_pairing_bonus || props.wallet.total_earnings || 0)
})

const directReferralBonusPercentage = computed(() => {
    return parseFloat(props.settings?.direct_referral_bonus_percentage ?? 35)
})

const pairingBonusPercentage = computed(() => {
    return parseFloat(props.settings?.pairing_bonus_percentage ?? 10)
})

</script>

<template>
    <UserLayout :page-title="'Binary MLM'" :page-section="'Wallet'">
        <div class="bw-page">

            <!-- Welcome strip -->
            <div class="bw-welcome anim-up" style="--d:0ms">
                <div class="bww-left">
                    <div class="bww-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                        </svg>
                    </div>
                    <div class="bww-text">
                        <span class="bww-eye">Binary MLM</span>
                        <h1 class="bww-title">DS-Credits Hub</h1>
                    </div>
                </div>
                <div class="bww-chips">
                    <div class="bww-chip bww-chip--purple">
                        <span class="bww-chip-lbl">Total</span>
                        <span class="bww-chip-val">{{ currencySymbol }}{{ formatCurrency(totalTokens) }}</span>
                    </div>
                    <div class="bww-chip bww-chip--green">
                        <span class="bww-chip-lbl">Available</span>
                        <span class="bww-chip-val">{{ currencySymbol }}{{ formatCurrency(availableTokens) }}</span>
                    </div>
                    <div class="bww-chip bww-chip--amber">
                        <span class="bww-chip-lbl">Locked</span>
                        <span class="bww-chip-val">{{ currencySymbol }}{{ formatCurrency(lockedTokens) }}</span>
                    </div>
                </div>
            </div>

            <!-- Hero token card -->
            <div class="bw-hero anim-up" style="--d:60ms">
                <div class="bw-hero-inner">
                    <!-- Left: balance info -->
                    <div class="bw-hero-left">
                        <div class="bw-hero-head">
                            <div class="bw-hero-icon">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </div>
                            <div>
                                <p class="bw-hero-sub">Total DS-Credits</p>
                                <p class="bw-hero-sub2">Investment Portfolio Balance</p>
                            </div>
                        </div>

                        <div class="bw-hero-amount">{{ currencySymbol }}{{ formatCurrency(totalTokens) }}</div>

                        <div class="bw-hero-badges">
                            <span class="bw-badge bw-badge--red">Non-Withdrawable</span>
                            <span class="bw-badge bw-badge--blue">Investment Only</span>
                        </div>

                        <div class="bw-quick-stats">
                            <div class="bw-qs-item">
                                <p class="bw-qs-lbl">Available</p>
                                <p class="bw-qs-val bw-qs-val--green">{{ formatCompact(availableTokens) }}</p>
                            </div>
                            <div class="bw-qs-item">
                                <p class="bw-qs-lbl">Locked</p>
                                <p class="bw-qs-val bw-qs-val--amber">{{ formatCompact(lockedTokens) }}</p>
                            </div>
                            <div class="bw-qs-item">
                                <p class="bw-qs-lbl">Earnings</p>
                                <p class="bw-qs-val bw-qs-val--purple">{{ formatCompact(totalEarnings) }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Right: circular SVG ring -->
                    <div class="bw-hero-ring-wrap">
                        <div class="bw-ring-container">
                            <svg class="bw-ring-svg" viewBox="0 0 256 256">
                                <circle cx="128" cy="128" r="110" fill="none"
                                    stroke="rgba(255,255,255,0.06)" stroke-width="14"/>
                                <circle cx="128" cy="128" r="110" fill="none"
                                    stroke="url(#bwGrad)" stroke-width="14"
                                    stroke-linecap="round"
                                    :stroke-dasharray="`${(availablePercentage / 100) * 691} 691`"
                                    class="bw-ring-progress"/>
                                <defs>
                                    <linearGradient id="bwGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%"   stop-color="#a75ded"/>
                                        <stop offset="50%"  stop-color="#7c3aed"/>
                                        <stop offset="100%" stop-color="#4ade80"/>
                                    </linearGradient>
                                </defs>
                            </svg>
                            <div class="bw-ring-center">
                                <p class="bw-ring-pct">{{ Math.round(availablePercentage) }}%</p>
                                <p class="bw-ring-lbl">Available</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Lock status bar -->
                <div v-if="hasActiveLock" class="bw-lock-bar">
                    <div class="bw-lock-head">
                        <div class="bw-lock-left">
                            <div class="bw-lock-icon">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                                </svg>
                            </div>
                            <div>
                                <p class="bw-lock-title">Security Lock Active</p>
                                <p class="bw-lock-sub">Unlocking in {{ getDaysRemaining(wallet.tokens_unlock_at) }} days</p>
                            </div>
                        </div>
                        <div class="bw-lock-pct">
                            <span>{{ Math.round(lockProgressPercentage) }}%</span>
                            <small>Progress</small>
                        </div>
                    </div>
                    <div class="bw-lock-track">
                        <div class="bw-lock-fill" :style="{ width: lockProgressPercentage + '%' }"></div>
                    </div>
                    <div class="bw-lock-dates">
                        <span>Locked: {{ formatDate(wallet.lock_started_at) }}</span>
                        <span>Unlocks: {{ formatDate(wallet.tokens_unlock_at) }}</span>
                    </div>
                </div>
            </div>

            <!-- Breakdown cards -->
            <div class="bw-breakdown anim-up" style="--d:120ms">
                <!-- Available -->
                <div class="bw-bd-card" style="--ac:34,197,94">
                    <div class="bw-bd-head">
                        <div class="bw-bd-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <span class="bw-bd-badge bw-bd-badge--green">Ready</span>
                    </div>
                    <p class="bw-bd-lbl">Available Credits</p>
                    <p class="bw-bd-val">{{ currencySymbol }}{{ formatCurrency(availableTokens) }}</p>
                    <p class="bw-bd-hint">Ready for new investments</p>
                    <div class="bw-bd-foot">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                        </svg>
                        Use to purchase packages
                    </div>
                </div>

                <!-- Locked -->
                <div class="bw-bd-card" style="--ac:251,191,36">
                    <div class="bw-bd-head">
                        <div class="bw-bd-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                            </svg>
                        </div>
                        <span v-if="hasLockedTokens" class="bw-bd-badge bw-bd-badge--amber">{{ getDaysRemaining(wallet.tokens_unlock_at) }}d left</span>
                    </div>
                    <p class="bw-bd-lbl">Locked Credits</p>
                    <p class="bw-bd-val">{{ currencySymbol }}{{ formatCurrency(lockedTokens) }}</p>
                    <p class="bw-bd-hint" v-if="hasLockedTokens">Unlocking in {{ getDaysRemaining(wallet.tokens_unlock_at) }} days</p>
                    <p class="bw-bd-hint" v-else>No locked credits</p>
                    <div class="bw-bd-foot">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        <span v-if="hasLockedTokens">{{ getHoursRemaining(wallet.tokens_unlock_at) }} hours remaining</span>
                        <span v-else>All credits available</span>
                    </div>
                </div>

                <!-- Earnings -->
                <div class="bw-bd-card" style="--ac:167,93,237">
                    <div class="bw-bd-head">
                        <div class="bw-bd-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
                            </svg>
                        </div>
                        <span class="bw-bd-badge bw-bd-badge--purple">Earned</span>
                    </div>
                    <p class="bw-bd-lbl">Total Earnings</p>
                    <p class="bw-bd-val">{{ currencySymbol }}{{ formatCurrency(totalEarnings) }}</p>
                    <p class="bw-bd-hint">All-time binary commissions</p>
                    <div class="bw-bd-foot">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                        </svg>
                        From pairing bonuses
                    </div>
                </div>
            </div>

            <!-- Info panels -->
            <div class="bw-info anim-up" style="--d:180ms">
                <!-- How it works -->
                <div class="bw-info-card">
                    <div class="bw-info-head">
                        <div class="bw-info-icon bw-info-icon--blue">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <h3 class="bw-info-title">How DS-Credits Work</h3>
                    </div>
                    <div class="bw-steps">
                        <div class="bw-step">
                            <div class="bw-step-num bw-step-num--blue">1</div>
                            <div>
                                <p class="bw-step-title">Earn Credits</p>
                                <p class="bw-step-desc">Receive DS-Credits from referral bonuses and binary pairing rewards</p>
                            </div>
                        </div>
                        <div class="bw-step">
                            <div class="bw-step-num bw-step-num--amber">2</div>
                            <div>
                                <p class="bw-step-title">Security Lock</p>
                                <p class="bw-step-desc">New credits may be locked for a security period (configurable by admin, default: no lock)</p>
                            </div>
                        </div>
                        <div class="bw-step">
                            <div class="bw-step-num bw-step-num--green">3</div>
                            <div>
                                <p class="bw-step-title">Reinvest</p>
                                <p class="bw-step-desc">Use available credits to purchase new investment packages and grow your portfolio</p>
                            </div>
                        </div>
                    </div>
                    <div class="bw-warn-note">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                        </svg>
                        <span><strong>Important:</strong> DS-Credits cannot be withdrawn. They are exclusively for reinvestment purposes.</span>
                    </div>
                </div>

                <!-- Ways to earn -->
                <div class="bw-info-card">
                    <div class="bw-info-head">
                        <div class="bw-info-icon bw-info-icon--purple">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"/>
                            </svg>
                        </div>
                        <h3 class="bw-info-title">Ways to Earn Credits</h3>
                    </div>
                    <div class="bw-earn-list">
                        <div class="bw-earn-item bw-earn-item--purple">
                            <div class="bw-earn-icon bw-earn-icon--purple">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                </svg>
                            </div>
                            <div>
                                <p class="bw-earn-title">Direct Referral Bonus</p>
                                <p class="bw-earn-desc">{{ directReferralBonusPercentage }}% of referred member's package</p>
                            </div>
                        </div>
                        <div class="bw-earn-item bw-earn-item--green">
                            <div class="bw-earn-icon bw-earn-icon--green">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"/>
                                </svg>
                            </div>
                            <div>
                                <p class="bw-earn-title">Binary Pairing Bonus</p>
                                <p class="bw-earn-desc">{{ pairingBonusPercentage }}% of weaker leg volume</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </UserLayout>
</template>

<style scoped>
/* ── animations ─────────────────────────────────────────────────── */
@keyframes slideUp {
    from { opacity:0; transform:translateY(22px) }
    to   { opacity:1; transform:none }
}
.anim-up {
    animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both;
    animation-delay: var(--d,0ms);
}

/* ── page ───────────────────────────────────────────────────────── */
.bw-page {
    max-width: 1280px;
    margin: 0 auto;
    padding: 14px 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
    z-index: 10;
    width: 100%;
}
@media (min-width:640px)  { .bw-page { padding: 18px 18px; gap: 14px; } }
@media (min-width:1024px) { .bw-page { padding: 20px 24px; gap: 16px; } }

/* ── welcome strip ──────────────────────────────────────────────── */
.bw-welcome {
    display:flex; align-items:center; justify-content:space-between;
    gap:12px; flex-wrap:wrap;
    padding:12px 16px; border-radius:13px;
    background:rgba(167,93,237,0.06);
    border:1px solid rgba(167,93,237,0.16);
}
.bww-left { display:flex; align-items:center; gap:10px; }
.bww-icon {
    width:40px; height:40px; border-radius:11px;
    background:linear-gradient(135deg,rgba(124,58,237,0.3),rgba(167,93,237,0.2));
    border:1px solid rgba(167,93,237,0.28);
    display:flex; align-items:center; justify-content:center;
    flex-shrink:0; color:#a75ded;
    box-shadow:0 0 14px rgba(167,93,237,0.22);
}
.bww-icon svg { width:18px; height:18px; }
.bww-eye   { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.1em; color:rgba(167,93,237,0.7); display:block; }
.bww-title { font-size:15px; font-weight:900; color:#fff; letter-spacing:-0.02em; margin:0; }
.bww-chips { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.bww-chip {
    display:flex; flex-direction:column; align-items:center; gap:2px;
    padding:6px 12px; border-radius:9px;
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
    min-width:58px;
}
.bww-chip-lbl { font-size:8px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.12em; }
.bww-chip-val { font-size:13px; font-weight:900; color:#fff; }
.bww-chip--purple .bww-chip-val { color:#c084fc; }
.bww-chip--green  .bww-chip-val { color:#4ade80; }
.bww-chip--amber  .bww-chip-val { color:#fbbf24; }

/* ── hero card ──────────────────────────────────────────────────── */
.bw-hero {
    background: rgba(10,4,22,0.97);
    border:1px solid rgba(167,93,237,0.13); border-radius:16px;
    overflow:hidden; position:relative; transition:border-color 0.25s;
}
.bw-hero::before {
    content:''; position:absolute; top:0; left:14%; right:14%; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}
.bw-hero:hover { border-color:rgba(167,93,237,0.24); }
.bw-hero-inner {
    display:flex; flex-direction:column; gap:20px; padding:18px;
}
@media (min-width:900px) { .bw-hero-inner { flex-direction:row; align-items:center; } }

.bw-hero-left { flex:1; display:flex; flex-direction:column; gap:14px; }
.bw-hero-head { display:flex; align-items:center; gap:12px; }
.bw-hero-icon {
    width:44px; height:44px; border-radius:12px;
    background:linear-gradient(135deg,rgba(167,93,237,0.3),rgba(124,58,237,0.2));
    border:1px solid rgba(167,93,237,0.4);
    display:flex; align-items:center; justify-content:center;
    flex-shrink:0; color:#c084fc;
}
.bw-hero-icon svg { width:22px; height:22px; }
.bw-hero-sub  { font-size:11px; font-weight:600; color:#4b5563; }
.bw-hero-sub2 { font-size:10px; color:#374151; margin-top:1px; }
.bw-hero-amount {
    font-size:2.5rem; font-weight:900; line-height:1.05;
    background:linear-gradient(135deg,#c084fc,#a75ded,#7c3aed);
    -webkit-background-clip:text; -webkit-text-fill-color:transparent;
    background-clip:text;
}
@media (min-width:640px) { .bw-hero-amount { font-size:3rem; } }

.bw-hero-badges { display:flex; flex-wrap:wrap; gap:6px; }
.bw-badge {
    display:inline-flex; padding:3px 10px; border-radius:9999px;
    font-size:10px; font-weight:700;
}
.bw-badge--red  { background:rgba(239,68,68,0.12); border:1px solid rgba(239,68,68,0.3); color:#f87171; }
.bw-badge--blue { background:rgba(59,130,246,0.12); border:1px solid rgba(59,130,246,0.3); color:#60a5fa; }

.bw-quick-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; }
.bw-qs-item {
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08);
    border-radius:10px; padding:10px 12px;
}
.bw-qs-lbl { font-size:9px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:3px; }
.bw-qs-val { font-size:1.25rem; font-weight:900; }
.bw-qs-val--green  { color:#4ade80; }
.bw-qs-val--amber  { color:#fbbf24; }
.bw-qs-val--purple { color:#c084fc; }

/* ring */
.bw-hero-ring-wrap { display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.bw-ring-container { position:relative; width:13rem; height:13rem; }
.bw-ring-svg { width:100%; height:100%; transform:rotate(-90deg); }
.bw-ring-progress { transition:stroke-dasharray 1s ease-out; }
.bw-ring-center {
    position:absolute; inset:0;
    display:flex; flex-direction:column; align-items:center; justify-content:center;
}
.bw-ring-pct { font-size:2.25rem; font-weight:900; color:#fff; line-height:1; }
.bw-ring-lbl { font-size:11px; color:rgba(255,255,255,0.45); margin-top:4px; }

/* lock bar */
.bw-lock-bar {
    margin:0 14px 14px;
    background:linear-gradient(135deg,rgba(251,191,36,0.08),rgba(234,88,12,0.06));
    border:1px solid rgba(251,191,36,0.2); border-radius:12px; padding:14px;
}
.bw-lock-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
.bw-lock-left { display:flex; align-items:center; gap:10px; }
.bw-lock-icon {
    width:32px; height:32px; border-radius:8px;
    background:rgba(251,191,36,0.15); border:1px solid rgba(251,191,36,0.3);
    display:flex; align-items:center; justify-content:center; color:#fbbf24; flex-shrink:0;
}
.bw-lock-icon svg { width:15px; height:15px; }
.bw-lock-title { font-size:12px; font-weight:700; color:#fff; }
.bw-lock-sub   { font-size:10px; color:rgba(255,255,255,0.45); margin-top:1px; }
.bw-lock-pct   { text-align:right; }
.bw-lock-pct span { font-size:1.25rem; font-weight:900; color:#fbbf24; display:block; }
.bw-lock-pct small { font-size:9px; color:rgba(255,255,255,0.35); }
.bw-lock-track { height:8px; background:rgba(0,0,0,0.3); border-radius:9999px; overflow:hidden; }
.bw-lock-fill {
    height:100%; border-radius:9999px;
    background:linear-gradient(90deg,#fbbf24,#f97316,#ef4444);
    transition:width 1s ease-out; position:relative;
}
.bw-lock-fill::after {
    content:''; position:absolute; inset:0; border-radius:inherit;
    background:rgba(255,255,255,0.25); animation:pulse 1.5s ease-in-out infinite;
}
@keyframes pulse { 0%,100%{opacity:.4} 50%{opacity:.9} }
.bw-lock-dates {
    display:flex; justify-content:space-between; margin-top:6px;
    font-size:10px; color:rgba(255,255,255,0.35);
}

/* ── breakdown cards ────────────────────────────────────────────── */
.bw-breakdown { display:grid; grid-template-columns:1fr; gap:10px; }
@media (min-width:640px)  { .bw-breakdown { grid-template-columns:1fr 1fr; } }
@media (min-width:1024px) { .bw-breakdown { grid-template-columns:repeat(3,1fr); } }

.bw-bd-card {
    position:relative;
    background:rgba(10,4,22,0.97);
    border:1px solid rgba(var(--ac),.2); border-radius:16px;
    padding:14px; overflow:hidden;
    transition:border-color .25s, transform .25s;
}
.bw-bd-card:hover { border-color:rgba(var(--ac),.45); transform:translateY(-2px); }
.bw-bd-card::before {
    content:''; position:absolute; top:0; left:14%; right:14%; height:1px;
    background:linear-gradient(90deg,transparent,rgba(var(--ac),.5),transparent);
}
.bw-bd-head { display:flex; align-items:start; justify-content:space-between; margin-bottom:10px; }
.bw-bd-icon {
    width:36px; height:36px; border-radius:9px;
    background:rgba(var(--ac),.15); border:1px solid rgba(var(--ac),.28);
    display:flex; align-items:center; justify-content:center; color:rgb(var(--ac));
}
.bw-bd-icon svg { width:16px; height:16px; }
.bw-bd-badge { font-size:9px; font-weight:700; padding:3px 8px; border-radius:9999px; }
.bw-bd-badge--green  { background:rgba(34,197,94,0.15);  border:1px solid rgba(34,197,94,0.3);  color:#4ade80; }
.bw-bd-badge--amber  { background:rgba(251,191,36,0.12); border:1px solid rgba(251,191,36,0.28); color:#fbbf24; }
.bw-bd-badge--purple { background:rgba(167,93,237,0.15); border:1px solid rgba(167,93,237,0.35); color:#c084fc; }
.bw-bd-lbl  { font-size:10px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:4px; }
.bw-bd-val  { font-size:22px; font-weight:900; color:#fff; margin-bottom:3px; }
.bw-bd-hint { font-size:10px; color:#374151; }
.bw-bd-foot {
    display:flex; align-items:center; gap:4px;
    margin-top:10px; padding-top:10px; border-top:1px solid rgba(255,255,255,0.06);
    font-size:10px; color:rgba(255,255,255,0.35);
}
.bw-bd-foot svg { width:12px; height:12px; flex-shrink:0; }

/* ── info panels ────────────────────────────────────────────────── */
.bw-info { display:grid; grid-template-columns:1fr; gap:12px; }
@media (min-width:900px) { .bw-info { grid-template-columns:1fr 1fr; } }

.bw-info-card {
    background:rgba(10,4,22,0.97);
    border:1px solid rgba(167,93,237,0.13); border-radius:16px;
    padding:16px; position:relative; overflow:hidden; transition:border-color 0.25s;
}
.bw-info-card::before {
    content:''; position:absolute; top:0; left:14%; right:14%; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}
.bw-info-card:hover { border-color:rgba(167,93,237,0.24); }
.bw-info-head { display:flex; align-items:center; gap:10px; margin-bottom:14px; }
.bw-info-icon {
    width:36px; height:36px; border-radius:9px;
    display:flex; align-items:center; justify-content:center; flex-shrink:0;
}
.bw-info-icon svg { width:16px; height:16px; }
.bw-info-icon--blue   { background:rgba(59,130,246,0.15); border:1px solid rgba(59,130,246,0.3); color:#60a5fa; }
.bw-info-icon--purple { background:rgba(167,93,237,0.15); border:1px solid rgba(167,93,237,0.3); color:#c084fc; }
.bw-info-title { font-size:13px; font-weight:800; color:#fff; letter-spacing:-0.01em; }

.bw-steps { display:flex; flex-direction:column; gap:10px; }
.bw-step  { display:flex; gap:10px; align-items:flex-start; }
.bw-step-num {
    width:28px; height:28px; border-radius:50%; flex-shrink:0;
    display:flex; align-items:center; justify-content:center;
    font-size:11px; font-weight:800;
}
.bw-step-num--blue  { background:rgba(59,130,246,0.15); border:1px solid rgba(59,130,246,0.3); color:#60a5fa; }
.bw-step-num--amber { background:rgba(251,191,36,0.12); border:1px solid rgba(251,191,36,0.28); color:#fbbf24; }
.bw-step-num--green { background:rgba(34,197,94,0.12);  border:1px solid rgba(34,197,94,0.3);   color:#4ade80; }
.bw-step-title { font-size:12px; font-weight:700; color:#fff; margin-bottom:2px; }
.bw-step-desc  { font-size:11px; color:rgba(255,255,255,0.45); line-height:1.5; }

.bw-warn-note {
    display:flex; gap:8px; align-items:flex-start;
    margin-top:12px; padding:12px; border-radius:10px;
    background:rgba(239,68,68,0.08); border:1px solid rgba(239,68,68,0.2);
    font-size:11px; color:#f87171; line-height:1.5;
}
.bw-warn-note svg { width:14px; height:14px; flex-shrink:0; margin-top:1px; }

.bw-earn-list { display:flex; flex-direction:column; gap:8px; }
.bw-earn-item {
    display:flex; gap:10px; align-items:center;
    padding:12px; border-radius:10px; border:1px solid; transition:border-color .2s;
}
.bw-earn-item--purple { background:rgba(167,93,237,0.06); border-color:rgba(167,93,237,0.18); }
.bw-earn-item--purple:hover { border-color:rgba(167,93,237,0.38); }
.bw-earn-item--green  { background:rgba(34,197,94,0.06); border-color:rgba(34,197,94,0.18); }
.bw-earn-item--green:hover  { border-color:rgba(34,197,94,0.38); }
.bw-earn-icon {
    width:36px; height:36px; border-radius:9px;
    display:flex; align-items:center; justify-content:center; flex-shrink:0;
}
.bw-earn-icon svg { width:15px; height:15px; }
.bw-earn-icon--purple { background:rgba(167,93,237,0.15); border:1px solid rgba(167,93,237,0.3); color:#c084fc; }
.bw-earn-icon--green  { background:rgba(34,197,94,0.12);  border:1px solid rgba(34,197,94,0.3);  color:#4ade80; }
.bw-earn-title { font-size:12px; font-weight:700; color:#fff; margin-bottom:2px; }
.bw-earn-desc  { font-size:11px; color:rgba(255,255,255,0.45); }
</style>
